import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ChevronRightIcon, MapPinIcon } from "@heroicons/react/24/outline";
import {
  EhDataTableShell,
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import { AssetStatusBadge } from "./AssetStatusBadge";
import type { HuelleAsset } from "./planModel";
import {
  HUELLE_DEFAULT_COLUMNS,
  clampHuelleColumnWidth,
  getHuelleColumnDefaultWidth,
  getHuelleColumnLabel,
  renderHuelleAssetCell,
  type HuelleColumnLabelMap,
} from "./columns";
import "./huelleDetail.css";

const ACTION_COL_WIDTH = 200;

export interface HuelleAssetTableProps {
  rows: HuelleAsset[];
  selectedAssetId: number | null;
  selectedSubLocationLabel: string;
  widths: Record<string, number>;
  /** Sichtbare Datenspalten (Reihenfolge = Anzeige). */
  columns?: string[];
  columnLabels?: HuelleColumnLabelMap;
  loading?: boolean;
  onSelectAsset: (assetId: number, subLocationId?: number | null) => void;
  /** Zeilenklick wählt aus und öffnet Stammdaten-Detail. */
  onOpenAsset?: (assetId: number, subLocationId?: number | null) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  /** Primäre Aktion je Zeile (z. B. „Wartung durchführen“). */
  rowAction?: {
    label: (asset: HuelleAsset) => string;
    disabled?: (asset: HuelleAsset) => boolean;
    busyAssetId?: number | null;
    onClick: (asset: HuelleAsset) => void;
  };
  emptyText?: React.ReactNode;
}

export function HuelleAssetTable(props: HuelleAssetTableProps) {
  return (
    <>
      <div className="hidden md:block">
        <DesktopTable {...props} />
      </div>
      <div className="md:hidden">
        <MobileCards {...props} />
      </div>
    </>
  );
}

function renderCell(item: HuelleAsset, col: string, locationFallback: string): React.ReactNode {
  if (col === "result_status") return <AssetStatusBadge asset={item} />;
  if (col === "plan") {
    return item.is_plan_positioned ? (
      <span className="inline-flex items-center gap-1 text-label-secondary">
        <MapPinIcon className="h-4 w-4 text-status-green" aria-hidden />
        Platziert
      </span>
    ) : (
      <span className="text-label-tertiary">—</span>
    );
  }
  const value = renderHuelleAssetCell(item, col, locationFallback);
  return value || <span className="text-label-tertiary">—</span>;
}

function DesktopThead({
  dataColumns,
  columnLabels,
  sort,
  onSort,
  activeResize,
  onResizePointerDown,
  hasAction,
}: {
  dataColumns: string[];
  columnLabels: HuelleColumnLabelMap;
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  hasAction: boolean;
}) {
  return (
    <thead className="eh-data-thead text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3.5 py-2.5 text-left font-semibold leading-snug"
          >
            <SortableHeader label={getHuelleColumnLabel(col, columnLabels)} columnKey={col} sort={sort} onSort={onSort} />
            <span
              className={clsx("eh-data-resizer", activeResize === col && "is-active")}
              role="separator"
              aria-orientation="vertical"
              title="Spaltenbreite ziehen"
              onPointerDown={(e) => onResizePointerDown(e, col, idx)}
            />
          </th>
        ))}
        {hasAction ? (
          <th scope="col" className="eh-data-th px-3.5 py-2.5 text-right font-semibold leading-snug">
            <span className="sr-only">Aktion</span>
          </th>
        ) : null}
      </tr>
    </thead>
  );
}

function DesktopTable({
  rows,
  selectedAssetId,
  selectedSubLocationLabel,
  widths,
  columns,
  columnLabels = {},
  loading = false,
  onSelectAsset,
  onOpenAsset,
  onColumnWidthChange,
  rowAction,
  emptyText = "Kein Objekt in diesem Bereich.",
}: HuelleAssetTableProps) {
  const dataColumns = columns?.length ? columns : HUELLE_DEFAULT_COLUMNS;
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () => sortRowsByKey(rows, sort, (row, key) => renderHuelleAssetCell(row, key, selectedSubLocationLabel)),
    [rows, selectedSubLocationLabel, sort],
  );
  const dataWidths = useMemo(
    () =>
      dataColumns.map((col) => {
        const stored = Number(widths[col]);
        const width =
          Number.isFinite(stored) && stored > 0 ? clampHuelleColumnWidth(stored) : getHuelleColumnDefaultWidth(col);
        if (col === "result_status" && width < 220) return 240;
        return width;
      }),
    [dataColumns, widths],
  );
  const hasAction = Boolean(rowAction);

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () => [dataColumns.join("|"), dataWidths.join(","), rows.length, selectedAssetId ?? "", hasAction].join("§"),
    [dataColumns, dataWidths, rows.length, selectedAssetId, hasAction],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: dataColumns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);
    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getHuelleColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampHuelleColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  function handleOpen(asset: HuelleAsset) {
    const id = Number(asset.asset_id);
    if (!Number.isFinite(id) || id <= 0) return;
    const subLocationId = asset.sub_location_id != null ? Number(asset.sub_location_id) : null;
    if (onOpenAsset) onOpenAsset(id, subLocationId);
    else onSelectAsset(id, subLocationId);
  }

  const colGroup = (refs: React.MutableRefObject<Record<string, HTMLTableColElement | null>>) => (
    <colgroup>
      {dataColumns.map((col, idx) => (
        <col
          key={col}
          ref={(el) => {
            refs.current[col] = el;
          }}
          style={{ width: `${dataWidths[idx]}px` }}
        />
      ))}
      {hasAction ? <col style={{ width: `${ACTION_COL_WIDTH}px` }} /> : null}
    </colgroup>
  );

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={reset} />
      <EhDataTableShell ref={tableWrapRef} className="huelle-table-shell rounded-none border-0 border-t border-separator shadow-none">
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          {colGroup(colRefs)}
          <DesktopThead
            dataColumns={dataColumns}
            columnLabels={columnLabels}
            sort={sort}
            onSort={toggle}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            hasAction={hasAction}
          />
          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={dataColumns.length + (hasAction ? 1 : 0)} className="px-3 py-10 text-center text-sm text-label-tertiary">
                  {emptyText}
                </td>
              </tr>
            )}
            {sortedRows.map((item) => {
              const id = Number(item.asset_id);
              const selected = selectedAssetId === id;
              const busy = rowAction?.busyAssetId === id;
              return (
                <tr
                  key={id}
                  tabIndex={0}
                  onClick={() => handleOpen(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpen(item);
                    }
                  }}
                  title="Details anzeigen"
                  aria-selected={selected}
                  className={clsx("eh-data-row huelle-asset-row align-middle", selected && "is-selected")}
                >
                  {dataColumns.map((col) => {
                    const text = renderHuelleAssetCell(item, col, selectedSubLocationLabel);
                    return (
                      <td
                        key={col}
                        className={clsx(
                          "eh-data-body-cell min-w-0 whitespace-normal break-words px-3.5 py-2.5 text-label",
                          col === "external_id" && "font-mono font-semibold",
                          col === "last_maintenance" && !item.last_performed_at && "text-label-secondary",
                        )}
                        title={text || undefined}
                      >
                        {renderCell(item, col, selectedSubLocationLabel)}
                      </td>
                    );
                  })}
                  {rowAction ? (
                    <td className="eh-data-body-cell px-3.5 py-2 text-right">
                      <button
                        type="button"
                        className="huelle-row-action"
                        disabled={busy || rowAction.disabled?.(item)}
                        onClick={(e) => {
                          e.stopPropagation();
                          rowAction.onClick(item);
                        }}
                      >
                        {busy ? "Starte …" : rowAction.label(item)}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{ top: floating.top, left: floating.left, width: floating.width }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                {colGroup(floatingColRefs)}
                <DesktopThead
                  dataColumns={dataColumns}
                  columnLabels={columnLabels}
                  sort={sort}
                  onSort={toggle}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  hasAction={hasAction}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function MobileCards({
  rows,
  selectedAssetId,
  selectedSubLocationLabel,
  columns,
  columnLabels = {},
  loading = false,
  onSelectAsset,
  onOpenAsset,
  rowAction,
  emptyText = "Kein Objekt in diesem Bereich.",
}: HuelleAssetTableProps) {
  const dataColumns = (columns?.length ? columns : HUELLE_DEFAULT_COLUMNS).filter(
    (col) => col !== "external_id" && col !== "name" && col !== "result_status",
  );

  if (rows.length === 0 && !loading) {
    return (
      <div className="m-3 rounded-[1rem] border border-dashed border-separator bg-card p-6 text-center text-sm text-label-tertiary">
        {emptyText}
      </div>
    );
  }

  return (
    <ul className="space-y-2.5 border-t border-separator px-3 py-3">
      {rows.map((item) => {
        const id = Number(item.asset_id);
        const selected = selectedAssetId === id;
        const externalId = renderHuelleAssetCell(item, "external_id", selectedSubLocationLabel);
        const name = renderHuelleAssetCell(item, "name", selectedSubLocationLabel);
        const subLocationId = item.sub_location_id != null ? Number(item.sub_location_id) : null;
        const busy = rowAction?.busyAssetId === id;
        return (
          <li key={id} className={clsx("huelle-mobile-card", selected && "is-selected")}>
            <button
              type="button"
              title="Details anzeigen"
              aria-pressed={selected}
              onClick={() => (onOpenAsset ? onOpenAsset(id, subLocationId) : onSelectAsset(id, subLocationId))}
              className="flex w-full flex-col gap-2 text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm font-semibold text-label">{externalId || "—"}</div>
                  <div className="mt-0.5 text-sm font-medium text-label">{name || "—"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <AssetStatusBadge asset={item} />
                  <ChevronRightIcon className="h-4 w-4 text-label-tertiary" aria-hidden />
                </div>
              </div>
              {dataColumns.length ? (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-label-secondary">
                  {dataColumns.map((col) => (
                    <div key={col} className="contents">
                      <dt className="text-label-tertiary">{getHuelleColumnLabel(col, columnLabels)}</dt>
                      <dd>{renderCell(item, col, selectedSubLocationLabel)}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </button>
            {rowAction ? (
              <button
                type="button"
                className="huelle-row-action mt-2 w-full justify-center"
                disabled={busy || rowAction.disabled?.(item)}
                onClick={() => rowAction.onClick(item)}
              >
                {busy ? "Starte …" : rowAction.label(item)}
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
